import { useState, useEffect, useRef, forwardRef } from "react";
import { HStack, Text, IconButton, Spinner, Box } from "@chakra-ui/react";
import { FaSyncAlt } from "react-icons/fa";
import AudioPlayer, { RHAP_UI } from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import "./AudioItem.css";

interface AudioItemProps {
  index: number;
  audioUrl: string;
  isActive: boolean;
  onPlaybackStart: (index: number) => void;
  onPlaybackEnd: (index: number) => void;
}

const AudioItem = forwardRef<AudioPlayer, AudioItemProps>(
  ({ index, audioUrl, isActive, onPlaybackStart, onPlaybackEnd }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const internalRef = useRef<AudioPlayer | null>(null);

    const setPlayerRef = (instance: AudioPlayer | null) => {
      internalRef.current = instance;
      if (typeof ref === "function") {
        ref(instance);
      } else if (ref) {
        (ref as React.MutableRefObject<AudioPlayer | null>).current = instance;
      }
    };

    const handleRetry = () => {
      setHasError(false);
      setIsLoading(true);
      const audio = internalRef.current?.audio?.current;
      if (audio) {
        audio.load();
        audio.play();
      }
    };

    useEffect(() => {
      if (isActive && internalRef.current?.audio?.current) {
        const audio = internalRef.current.audio.current;
        if (audio.paused && audio.readyState >= 2) {
          audio.play();
        }
      }
    }, [isActive]);

    return (
      <HStack
        width="100%"
        padding="10px"
        borderRadius="5px"
        backgroundColor={isActive ? "#E6F7FF" : "white"}
        border={isActive ? "2px solid #0096e0" : "1px solid lightgrey"}
        transition="all 0.2s"
        spacing={3}
      >
        <Text
          fontWeight="bold"
          minWidth="30px"
          fontSize="md"
          color={isActive ? "#0096e0" : "inherit"}
        >
          {index + 1}.
        </Text>

        <Box flex="1" display="flex" alignItems="center" gap="10px">
          {isLoading && (
            <Spinner
              size="sm"
              color="#0096e0"
              thickness="3px"
              aria-label={`Loading audio ${index + 1}`}
            />
          )}

          {hasError && (
            <IconButton
              icon={<FaSyncAlt />}
              aria-label={`Retry loading audio ${index + 1}`}
              size="sm"
              onClick={handleRetry}
              colorScheme="red"
              variant="ghost"
              _hover={{ backgroundColor: "#FFE6E6" }}
            />
          )}

          <Box
            flex="1"
            className="audio-player-wrapper"
            display={isLoading || hasError ? "none" : "block"}
          >
            <AudioPlayer
              ref={setPlayerRef}
              src={audioUrl}
              preload="metadata"
              autoPlay={isActive}
              autoPlayAfterSrcChange={isActive}
              showJumpControls={false}
              customProgressBarSection={[
                RHAP_UI.PROGRESS_BAR,
                RHAP_UI.CURRENT_TIME,
                RHAP_UI.DURATION,
              ]}
              customControlsSection={[RHAP_UI.MAIN_CONTROLS]}
              customAdditionalControls={[]}
              customVolumeControls={[]}
              onPlay={() => onPlaybackStart(index)}
              onEnded={() => onPlaybackEnd(index)}
              onLoadStart={() => setIsLoading(true)}
              onCanPlay={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
            />
          </Box>
        </Box>
      </HStack>
    );
  }
);

AudioItem.displayName = "AudioItem";

export default AudioItem;
